// ─── Shared styles — earthy orange palette ────────────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i) => <div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th, width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i) => (
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO), ...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO), ...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — What is Multiplex Arrangement?
// Side-by-side: single motor vs parallel (multiplex) arrangement
// showing torque/power summing concept
// ─────────────────────────────────────────────────────────────────────
function DiagramConcept() {
  const W = 960, H = 380;

  // Draw a simple motor symbol
  function motorSymbol(cx, cy, r, label, col) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={r} fill={col||'#d4874a'} stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={cx} y={cy - 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">M</text>
        <text x={cx} y={cy + 9} textAnchor="middle" fontSize="7.5" fill="#fff">{label}</text>
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multiplex Motor Arrangement — Concept: Single Motor vs Parallel Motor Array</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arC" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arCg" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
          <pattern id="hatchC" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#d4874a" strokeWidth="1.5" opacity="0.4"/>
          </pattern>
        </defs>

        {/* ── LEFT: Single Large Motor ── */}
        <text x={180} y={22} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Single Motor</text>

        {/* Large motor */}
        <circle cx={120} cy={170} r={55} fill="#d4874a" stroke="#7a2e00" strokeWidth="3"/>
        <text x={120} y={165} textAnchor="middle" fontSize="13" fontWeight="700" fill="#7a2e00">M</text>
        <text x={120} y={182} textAnchor="middle" fontSize="9" fill="#fff">100 kW</text>
        <text x={120} y={195} textAnchor="middle" fontSize="8.5" fill="#fdf0e4">τ_rated</text>

        {/* Output shaft */}
        <line x1={175} y1={170} x2={250} y2={170} stroke="#7a2e00" strokeWidth="5"/>
        <rect x={250} y={155} width={50} height={30} rx={4} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={275} y={174} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">Load</text>

        {/* Power arrow */}
        <line x1={300} y1={170} x2={340} y2={170} stroke="#c45a10" strokeWidth="2" markerEnd="url(#arC)"/>
        <text x={322} y={162} textAnchor="middle" fontSize="8" fill="#c45a10" fontWeight="700">P</text>

        {/* Single point of failure annotation */}
        <rect x={30} y={265} width={200} height={50} rx={4} fill="#fdf0e4" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={130} y={283} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c0392b">Limitations:</text>
        <text x={130} y={296} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Single point of failure</text>
        <text x={130} y={308} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Large, heavy, costly motor</text>

        {/* ── DIVIDER ── */}
        <line x1={370} y1={10} x2={370} y2={H-10} stroke="#e8b07a" strokeWidth="1.5" strokeDasharray="6,4"/>
        <text x={370} y={200} textAnchor="middle" fontSize="22" fill="#c45a10" fontWeight="700"
          transform={`rotate(-90,370,200)`}>vs</text>

        {/* ── RIGHT: Multiplex / Parallel Motors ── */}
        <text x={670} y={22} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Multiplex Arrangement (4 × 25 kW)</text>

        {/* Four smaller motors stacked */}
        {[0,1,2,3].map(i => {
          const my = 75 + i * 68;
          return (
            <g key={i}>
              {motorSymbol(450, my, 25, `25kW`, '#c45a10')}
              {/* Shaft to combiner */}
              <line x1={475} y1={my} x2={560} y2={my} stroke="#7a2e00" strokeWidth="3.5"/>
              {/* Torque label */}
              <text x={518} y={my - 8} textAnchor="middle" fontSize="8" fill="#7a4a28">τ/4</text>
            </g>
          );
        })}

        {/* Combiner / gearbox */}
        <rect x={560} y={60} width={60} height={265} rx={6} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.7"/>
        <text x={590} y={182} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00" transform={`rotate(-90,590,182)`}>COMBINER</text>

        {/* Combined output shaft */}
        <line x1={620} y1={192} x2={700} y2={192} stroke="#7a2e00" strokeWidth="5"/>

        {/* Load */}
        <rect x={700} y={177} width={55} height={30} rx={4} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={727} y={196} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">Load</text>

        {/* Output annotation */}
        <line x1={755} y1={192} x2={800} y2={192} stroke="#2a7a3b" strokeWidth="2.5" markerEnd="url(#arCg)"/>
        <text x={780} y={184} textAnchor="middle" fontSize="8" fill="#2a7a3b" fontWeight="700">P_total</text>
        <text x={780} y={196} textAnchor="middle" fontSize="7.5" fill="#2a7a3b">= 4 × P</text>

        {/* Torque summing label */}
        <rect x={620} y={60} width={145} height={52} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={692} y={76} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Power summed:</text>
        <text x={692} y={89} textAnchor="middle" fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">τ_out = Σ τᵢ</text>
        <text x={692} y={102} textAnchor="middle" fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">P_out = Σ Pᵢ</text>

        {/* Benefits annotation */}
        <rect x={615} y={265} width={215} height={72} rx={4} fill="#fdf0e4" stroke="#2a7a3b" strokeWidth="1.5"/>
        <text x={722} y={281} textAnchor="middle" fontSize="9" fontWeight="700" fill="#2a7a3b">Advantages:</text>
        <text x={722} y={294} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Redundancy — N-1 motor failure</text>
        <text x={722} y={306} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Smaller motors — standard stock</text>
        <text x={722} y={318} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Scalable power by adding units</text>
        <text x={722} y={330} textAnchor="middle" fontSize="8.5" fill="#7a4a28">Load sharing across units</text>

        {/* Power flow arrows into combiner */}
        {[0,1,2,3].map(i => {
          const my = 75 + i * 68;
          return <line key={i} x1={555} y1={my} x2={562} y2={my} stroke="#2a7a3b" strokeWidth="2" markerEnd="url(#arCg)"/>;
        })}
      </svg>
      <div style={S.dCaption}>
        A multiplex arrangement uses multiple smaller motors acting on a common load through a combining mechanism. The total output power equals the sum of all motor contributions, while any single motor failure reduces — but does not eliminate — output capacity.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Torque Summing Methods
// Four coupling topologies: parallel shaft, epicyclic, tandem shaft, differential
// ─────────────────────────────────────────────────────────────────────
function DiagramTorqueSumming() {
  const W = 1050, H = 440;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Torque-Summing Topologies — Parallel Shaft · Epicyclic · Tandem · Differential</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arTS" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arTSg" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
        </defs>

        {/* ═══ TOP-LEFT: PARALLEL SHAFT ═══ */}
        <text x={120} y={20} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Parallel Shaft Gear Sum</text>
        <circle cx={30} cy={100} r={20} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={30} y={104} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">M1</text>
        <line x1={50} y1={100} x2={75} y2={100} stroke="#7a2e00" strokeWidth="3"/>
        <circle cx={30} cy={170} r={20} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={30} y={174} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">M2</text>
        <line x1={50} y1={170} x2={75} y2={170} stroke="#7a2e00" strokeWidth="3"/>
        <circle cx={87} cy={100} r={14} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        <text x={87} y={104} textAnchor="middle" fontSize="7" fill="#7a2e00">G1</text>
        <circle cx={87} cy={170} r={14} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        <text x={87} y={174} textAnchor="middle" fontSize="7" fill="#7a2e00">G2</text>
        <circle cx={135} cy={135} r={35} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2.5" opacity="0.85"/>
        <text x={135} y={132} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">Output</text>
        <text x={135} y={143} textAnchor="middle" fontSize="7.5" fill="#7a2e00">Gear</text>
        <line x1={100} y1={107} x2={104} y2={111} stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="2,2"/>
        <line x1={100} y1={162} x2={104} y2={158} stroke="#7a2e00" strokeWidth="1.5" strokeDasharray="2,2"/>
        <line x1={170} y1={135} x2={218} y2={135} stroke="#7a2e00" strokeWidth="4"/>
        <text x={194} y={128} textAnchor="middle" fontSize="8" fill="#2a7a3b" fontWeight="700">τ₁+τ₂</text>
        <text x={120} y={205} textAnchor="middle" fontSize="8" fill="#7a4a28">Both input gears mesh</text>
        <text x={120} y={217} textAnchor="middle" fontSize="7.5" fill="#a04010">on common output gear</text>
        <text x={120} y={229} textAnchor="middle" fontSize="7.5" fill="#7a4a28">Torques add algebraically</text>

        {/* ═══ TOP-RIGHT: EPICYCLIC ═══
             Shifted slightly right so M1 at top doesn't crowd left divider */}
        <text x={390} y={20} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Epicyclic (Planetary) Sum</text>
        {/* Ring gear */}
        <circle cx={390} cy={130} r={68} fill="none" stroke="#7a2e00" strokeWidth="3.5"/>
        <circle cx={390} cy={130} r={62} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
        {/* Ring label — placed OUTSIDE on right side to avoid overlap */}
        <text x={464} y={133} fontSize="8" fill="#7a2e00" textAnchor="start">Ring</text>
        <text x={464} y={144} fontSize="8" fill="#7a2e00" textAnchor="start">(fixed)</text>
        {/* Sun gear */}
        <circle cx={390} cy={130} r={18} fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={390} y={134} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">Sun</text>
        {/* Planet gears */}
        {[0, 120, 240].map((deg, i) => {
          const a = deg * Math.PI / 180;
          const px = 390 + 40 * Math.cos(a), py = 130 + 40 * Math.sin(a);
          return (
            <g key={i}>
              <circle cx={px} cy={py} r={14} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
              <text x={px} y={py+4} textAnchor="middle" fontSize="7.5" fill="#7a2e00">P</text>
            </g>
          );
        })}
        {/* Carrier dashed circle */}
        <circle cx={390} cy={130} r={40} fill="none" stroke="#2a7a3b" strokeWidth="2" strokeDasharray="5,3"/>
        {/* M1 sun input — above ring, connected by dashed line */}
        <line x1={390} y1={130} x2={390} y2={67} stroke="#2a7a3b" strokeWidth="2" strokeDasharray="4,3"/>
        <circle cx={390} cy={48} r={16} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={390} y={52} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M1</text>
        <text x={408} y={52} fontSize="7.5" fill="#2a7a3b">sun</text>
        {/* M2 ring input — from right, well outside ring radius (ring right edge = 390+68=458) */}
        <line x1={458} y1={130} x2={488} y2={130} stroke="#7a2e00" strokeWidth="3"/>
        <circle cx={504} cy={130} r={16} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={504} y={134} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M2</text>
        <text x={504} y={152} textAnchor="middle" fontSize="7.5" fill="#7a4a28">ring input</text>
        {/* Carrier output — downward */}
        <line x1={390} y1={198} x2={390} y2={222} stroke="#2a7a3b" strokeWidth="3.5" markerEnd="url(#arTSg)"/>
        <text x={408} y={215} fontSize="8" fill="#2a7a3b" fontWeight="700">Carrier out</text>
        <text x={390} y={238} textAnchor="middle" fontSize="8" fill="#7a4a28">Sun + Ring drive</text>
        <text x={390} y={250} textAnchor="middle" fontSize="7.5" fill="#a04010">carrier sums both inputs</text>

        {/* ═══ BOTTOM-LEFT: TANDEM SHAFT ═══ */}
        <text x={120} y={275} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Tandem (Series Shaft)</text>
        <circle cx={30} cy={340} r={20} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={30} y={344} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M1</text>
        <line x1={50} y1={340} x2={90} y2={340} stroke="#7a2e00" strokeWidth="5"/>
        <circle cx={120} cy={340} r={20} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={120} y={344} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M2</text>
        <circle cx={120} cy={340} r={30} fill="none" stroke="#c45a10" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7"/>
        <line x1={90} y1={340} x2={215} y2={340} stroke="#7a2e00" strokeWidth="5"/>
        <rect x={215} y={327} width={38} height={26} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={234} y={344} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">Out</text>
        <text x={120} y={395} textAnchor="middle" fontSize="8" fill="#7a4a28">Both motors on same shaft</text>
        <text x={120} y={407} textAnchor="middle" fontSize="7.5" fill="#a04010">Second motor hollow rotor</text>
        <text x={120} y={419} textAnchor="middle" fontSize="7.5" fill="#7a4a28">Simple — no combining gears</text>

        {/* ═══ BOTTOM-RIGHT: TORQUE DIFFERENTIAL ═══
             Shifted to cx=780 so title fits and output arrow stays in view */}
        <text x={780} y={275} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Differential Torque Summing</text>
        <ellipse cx={780} cy={360} rx={70} ry={55} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={780} y={353} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Differential</text>
        <text x={780} y={367} textAnchor="middle" fontSize="8" fill="#7a4a28">Gear Set</text>
        {/* M1 input from left */}
        <line x1={655} y1={360} x2={712} y2={360} stroke="#7a2e00" strokeWidth="4"/>
        <circle cx={640} cy={360} r={18} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={640} y={364} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M1</text>
        {/* M2 input from top */}
        <line x1={780} y1={295} x2={780} y2={307} stroke="#7a2e00" strokeWidth="4"/>
        <circle cx={780} cy={280} r={18} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={780} y={284} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M2</text>
        {/* Output right — stays within W=1050 */}
        <line x1={848} y1={360} x2={910} y2={360} stroke="#2a7a3b" strokeWidth="4" markerEnd="url(#arTSg)"/>
        <text x={880} y={352} textAnchor="middle" fontSize="8" fill="#2a7a3b" fontWeight="700">Output</text>
        <text x={880} y={364} textAnchor="middle" fontSize="7.5" fill="#2a7a3b">τ₁ + τ₂</text>
        <text x={780} y={430} textAnchor="middle" fontSize="8" fill="#7a4a28">Differential combines two</text>
        <text x={780} y={442} textAnchor="middle" fontSize="7.5" fill="#a04010">independent input torques</text>

        {/* Dividers */}
        <line x1={260} y1={10} x2={260} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
        <line x1={260} y1={260} x2={1050} y2={260} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
        <line x1={550} y1={260} x2={550} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
      </svg>
      <div style={S.dCaption}>
        Torque summing can be achieved through parallel gear meshes, epicyclic (planetary) gearsets where sun and ring independently drive the carrier, tandem motor-on-shaft arrangements, or bevel differential gearsets.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Load Sharing & Torque Balance
// Graph showing load sharing curves and the effect of speed droop
// ─────────────────────────────────────────────────────────────────────
function DiagramLoadSharing() {
  const W = 960, H = 360;

  // Chart area
  const cX = 60, cY = 30, cW = 440, cH = 260;

  // Speed-torque curve: ω = ω_no_load - (ω_no_load - ω_full_load)/T_rated × T
  // Motor A: stiffer (less droop), Motor B: more droop
  function motorCurve(tRated, omegaNL, omegaFL, nPts) {
    const pts = [];
    for (let i = 0; i <= nPts; i++) {
      const t = (i / nPts) * tRated * 1.2;
      const omega = omegaNL - (omegaNL - omegaFL) / tRated * Math.min(t, tRated);
      const x = cX + (t / (tRated * 1.2)) * cW;
      const y = cY + cH - ((omega - (omegaFL * 0.5)) / (omegaNL - omegaFL * 0.5)) * cH;
      pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
    return pts.join(' ');
  }

  const T_rated = 100;
  const omegaNL = 100, omegaFL = 80;   // Motor A: low droop (stiff)
  const omegaNLb = 100, omegaFLb = 60; // Motor B: high droop

  const tTicks = [0, 20, 40, 60, 80, 100, 120];
  const wTicks = [60, 70, 80, 90, 100];

  // Equal load sharing point
  const eqT = 60, eqW = 88;
  const eqX = cX + (eqT / 120) * cW;
  const eqY = cY + cH - ((eqW - 60) / 40) * cH;

  // Unequal sharing (parallel motors, different droops)
  const t1 = 75, t2 = 45;
  const x1 = cX + (t1 / 120) * cW;
  const x2 = cX + (t2 / 120) * cW;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Load Sharing &amp; Torque Balance — Speed-Droop Curves &amp; Equalisation Methods</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arLS" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#7a2e00"/>
          </marker>
        </defs>

        {/* Chart background */}
        <rect x={cX} y={cY} width={cW} height={cH} fill="#fdf6ee" stroke="#e8b07a" strokeWidth="1"/>

        {/* Grid */}
        {tTicks.map((t,i) => {
          const x = cX + (t / 120) * cW;
          return (
            <g key={i}>
              <line x1={x} y1={cY} x2={x} y2={cY+cH} stroke="#e8c89a" strokeWidth="0.8" strokeDasharray="3,3"/>
              <text x={x} y={cY+cH+14} textAnchor="middle" fontSize="8" fill="#7a4a28">{t}</text>
            </g>
          );
        })}
        {wTicks.map((w,i) => {
          const y = cY + cH - ((w - 60) / 40) * cH;
          return (
            <g key={i}>
              <line x1={cX} y1={y} x2={cX+cW} y2={y} stroke="#e8c89a" strokeWidth="0.8" strokeDasharray="3,3"/>
              <text x={cX-6} y={y+4} textAnchor="end" fontSize="8" fill="#7a4a28">{w}</text>
            </g>
          );
        })}

        {/* Motor A curve (stiff / low droop) */}
        <polyline points={motorCurve(T_rated, omegaNL, omegaFL, 60)}
          fill="none" stroke="#c45a10" strokeWidth="2.5"/>
        {/* Legend inside chart — top-right, stacked with clear separation */}
        {/* Motor A box: y = cY+8 to cY+44 */}
        <rect x={cX+cW-112} y={cY+8} width={110} height={34} rx={3} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <line x1={cX+cW-108} y1={cY+20} x2={cX+cW-88} y2={cY+20} stroke="#c45a10" strokeWidth="2.5"/>
        <text x={cX+cW-84} y={cY+24} fontSize="9" fontWeight="700" fill="#c45a10">Motor A</text>
        <text x={cX+cW-84} y={cY+37} fontSize="8" fill="#a04010">(low droop)</text>

        {/* Motor B curve (high droop) */}
        <polyline points={motorCurve(T_rated, omegaNLb, omegaFLb, 60)}
          fill="none" stroke="#2a6496" strokeWidth="2.5"/>
        {/* Motor B box: y = cY+48 to cY+82  — 4px gap below Motor A box */}
        <rect x={cX+cW-112} y={cY+48} width={110} height={34} rx={3} fill="#fdf0e4" stroke="#2a6496" strokeWidth="1.2"/>
        <line x1={cX+cW-108} y1={cY+60} x2={cX+cW-88} y2={cY+60} stroke="#2a6496" strokeWidth="2.5"/>
        <text x={cX+cW-84} y={cY+64} fontSize="9" fontWeight="700" fill="#2a6496">Motor B</text>
        <text x={cX+cW-84} y={cY+77} fontSize="8" fill="#2a6496">(high droop)</text>

        {/* Equal sharing line (where curves cross, ω is same) */}
        <line x1={cX} y1={eqY} x2={cX+cW} y2={eqY}
          stroke="#2a7a3b" strokeWidth="1.5" strokeDasharray="6,3"/>
        <text x={cX + 10} y={eqY - 5} fontSize="8" fontWeight="700" fill="#2a7a3b">Equal speed line → equal share</text>

        {/* Unequal sharing annotation */}
        <line x1={x2} y1={cY} x2={x2} y2={cY+cH} stroke="#2a6496" strokeWidth="1.2" strokeDasharray="4,3"/>
        <line x1={x1} y1={cY} x2={x1} y2={cY+cH} stroke="#c45a10" strokeWidth="1.2" strokeDasharray="4,3"/>
        <rect x={x2-5} y={cY+8} width={x1-x2+10} height={18} rx={3} fill="#fdf0e4" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={(x1+x2)/2} y={cY+20} textAnchor="middle" fontSize="8" fontWeight="700" fill="#c0392b">Unequal sharing!</text>

        {/* Axis labels */}
        <text x={cX+cW/2} y={cY+cH+28} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Torque T (% rated)</text>
        <text x={cX-42} y={cY+cH/2} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00"
          transform={`rotate(-90,${cX-42},${cY+cH/2})`}>Speed ω (%)</text>

        {/* Droop annotation — bottom-right corner, below the curves */}
        {(() => {
          const y0   = cY + cH - ((omegaNL  - 60)/40)*cH;  // Motor A at T=rated
          const yFull = cY + cH - ((omegaFL  - 60)/40)*cH;
          // Place arrow at x = cX+cW*0.88, shifted down to bottom quarter
          const ax = cX + cW * 0.88;
          return (
            <g>
              <line x1={ax} y1={y0} x2={ax} y2={yFull}
                stroke="#c45a10" strokeWidth="1.5" markerEnd="url(#arLS)" markerStart="url(#arLS)"/>
              <text x={ax+5} y={(y0+yFull)/2+4}  fontSize="8.5" fontWeight="700" fill="#c45a10">Droop</text>
              <text x={ax+5} y={(y0+yFull)/2+16} fontSize="7.5"               fill="#a04010">Δω/ω_NL</text>
            </g>
          );
        })()}

        {/* Right panel: methods to achieve equal load sharing */}
        <rect x={530} y={10} width={422} height={H-20} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={741} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Load Equalisation Methods</text>
        <line x1={538} y1={34} x2={944} y2={34} stroke="#e8b07a"/>
        {[
          ['Droop control:','Each motor has speed-torque slope'],
          ['','Parallel motors naturally share load'],
          ['','if identical droop characteristics'],
          ['','Problem: unequal droop → unequal share'],
          ['',''],
          ['Cross-coupling:','Current/torque feedback loop'],
          ['','Adjusts each motor to match average'],
          ['','torque of the group'],
          ['','Fast electronic response needed'],
          ['',''],
          ['Master-slave:','One motor (master) sets speed ref'],
          ['','Slaves match torque of master'],
          ['','Simple but master is critical point'],
          ['',''],
          ['Load sharing ctrl:','All motors share a common torque'],
          ['','setpoint from central controller'],
          ['','Most flexible — handles dissimilar'],
          ['','motors and varying loads'],
          ['',''],
          ['Mechanical coupling:','Rigid coupling forces identical speed'],
          ['','Torque distributes by motor stiffness'],
          ['','Risk: circulating currents if speed mis-match'],
        ].map(([l,v],i) => (
          <g key={i}>
            <text x={538} y={48+i*13} fontSize="9" fontWeight={l&&v===''?'700':'400'} fill={l?'#7a2e00':'#7a4a28'}>{l}</text>
            <text x={l&&v?640:538} y={48+i*13} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Speed droop determines how load sharing occurs between parallel motors. Identical droop characteristics produce equal sharing. Electronic cross-coupling or master-slave control overcomes mismatches in motor characteristics.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Marine Twin-Engine Arrangement (Z-drive / propeller shaft)
// Classic application: twin engines on one shaft / twin propellers
// ─────────────────────────────────────────────────────────────────────
function DiagramMarineArrangement() {
  const W = 960, H = 400;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Marine Multiplex Arrangement — Twin-Engine Gearbox &amp; Propeller Shaft (top-down view)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arMR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arMRg" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
          <pattern id="hullHatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(0)">
            <line x1="0" y1="0" x2="8" y2="0" stroke="#d4874a" strokeWidth="1" opacity="0.3"/>
          </pattern>
        </defs>

        {/* ── Hull outline (simplified ship hull, top view) ── */}
        <path d="M 60 200 Q 100 100 300 80 L 700 80 Q 900 80 920 200 Q 900 320 700 320 L 300 320 Q 100 300 60 200 Z"
          fill="url(#hullHatch)" stroke="#7a2e00" strokeWidth="2.5" opacity="0.5"/>
        <text x={490} y={210} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00" opacity="0.4">VESSEL HULL (top view)</text>

        {/* ── Port Engine (top) ── */}
        <rect x={160} y={108} width={110} height={55} rx={5} fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={215} y={132} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">Port Engine</text>
        <text x={215} y={146} textAnchor="middle" fontSize="8" fill="#fdf0e4">M1 · 1500 kW</text>

        {/* ── Starboard Engine (bottom) ── */}
        <rect x={160} y={237} width={110} height={55} rx={5} fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={215} y={261} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">Stbd Engine</text>
        <text x={215} y={275} textAnchor="middle" fontSize="8" fill="#fdf0e4">M2 · 1500 kW</text>

        {/* ── Combining Gearbox ── */}
        <rect x={320} y={158} width={120} height={84} rx={6} fill="#d4874a" stroke="#7a2e00" strokeWidth="3"/>
        <text x={380} y={196} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Combining</text>
        <text x={380} y={210} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Gearbox</text>
        <text x={380} y={224} textAnchor="middle" fontSize="8" fill="#7a4a28">3000 kW output</text>

        {/* ── Shaft lines from engines to gearbox ── */}
        <line x1={270} y1={135} x2={320} y2={178} stroke="#7a2e00" strokeWidth="4"/>
        <line x1={270} y1={265} x2={320} y2={222} stroke="#7a2e00" strokeWidth="4"/>

        {/* Flexible couplings on shaft lines */}
        <ellipse cx={293} cy={156} rx={8} ry={12} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <ellipse cx={293} cy={244} rx={8} ry={12} fill="#e8b07a" stroke="#7a2e00" strokeWidth="2"/>
        <text x={305} y={156} fontSize="7.5" fill="#7a4a28">flex</text>
        <text x={305} y={244} fontSize="7.5" fill="#7a4a28">flex</text>

        {/* ── Propeller shaft ── */}
        <line x1={440} y1={200} x2={700} y2={200} stroke="#7a2e00" strokeWidth="6"/>

        {/* Shaft bearing supports */}
        {[500, 580, 660].map((x,i) => (
          <g key={i}>
            <rect x={x-6} y={188} width={12} height={24} rx={2} fill="#d4874a" stroke="#7a2e00" strokeWidth="1.5"/>
            <text x={x} y={222} textAnchor="middle" fontSize="7" fill="#7a4a28">brg</text>
          </g>
        ))}

        {/* ── Propeller ── */}
        {[0, 72, 144, 216, 288].map((deg, i) => {
          const a = deg * Math.PI / 180;
          const bx = 700 + 55 * Math.cos(a), by = 200 + 55 * Math.sin(a);
          return (
            <ellipse key={i} cx={(700+bx)/2} cy={(200+by)/2}
              rx={28} ry={9}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.8"
              transform={`rotate(${deg}, ${(700+bx)/2}, ${(200+by)/2})`}/>
          );
        })}
        <circle cx={700} cy={200} r={12} fill="#7a2e00"/>
        <text x={760} y={204} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Propeller</text>

        {/* ── Annotations ── */}
        {/* Speed/torque at gearbox output */}
        <line x1={380} y1={158} x2={380} y2={120} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={318} y={96} width={124} height={26} rx={3} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={380} y={109} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Reduction ratio</text>
        <text x={380} y={119} textAnchor="middle" fontSize="8" fill="#a04010">e.g. 6:1 · low-speed shaft</text>

        {/* Thrust bearing */}
        <rect x={455} y={186} width={20} height={28} rx={3} fill="#2a6496" stroke="#7a2e00" strokeWidth="2"/>
        <text x={465} y={240} textAnchor="middle" fontSize="7.5" fill="#2a6496">Thrust</text>
        <text x={465} y={250} textAnchor="middle" fontSize="7.5" fill="#2a6496">bearing</text>
        <line x1={465} y1={214} x2={465} y2={236} stroke="#2a6496" strokeWidth="1"/>

        {/* Clutches */}
        <rect x={283} y={130} width={22} height={22} rx={3} fill="#7a2e00" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
        <text x={294} y={145} textAnchor="middle" fontSize="7" fontWeight="700" fill="#fff">CL</text>
        <rect x={283} y={248} width={22} height={22} rx={3} fill="#7a2e00" stroke="#7a2e00" strokeWidth="1.5" opacity="0.7"/>
        <text x={294} y={263} textAnchor="middle" fontSize="7" fontWeight="700" fill="#fff">CL</text>

        {/* Clutch label */}
        <line x1={283} y1={141} x2={240} y2={115} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={160} y={96} width={82} height={20} rx={3} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={201} y={109} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">Clutch (×2)</text>

        {/* Labels at left */}
        <text x={148} y={105} textAnchor="end" fontSize="8.5" fontWeight="700" fill="#7a2e00">PORT</text>
        <text x={148} y={275} textAnchor="end" fontSize="8.5" fontWeight="700" fill="#7a2e00">STBD</text>
        <text x={148} y={190} textAnchor="end" fontSize="8.5" fill="#7a4a28">← BOW</text>
        <text x={880} y={204} fontSize="8.5" fontWeight="700" fill="#7a2e00">STERN →</text>

        {/* Right info panel */}
        <rect x={790} y={84} width={162} height={230} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={871} y={100} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#7a2e00">Operating Modes</text>
        <line x1={797} y1={106} x2={944} y2={106} stroke="#e8b07a"/>
        {[
          ['Full power:','Both engines in'],
          ['','3000 kW total'],
          ['',''],
          ['Cruise:','One engine in'],
          ['','1500 kW (50%)'],
          ['','Full fuel efficiency'],
          ['',''],
          ['Harbour:','Either engine'],
          ['','Clutch other out'],
          ['',''],
          ['Emergency:','One engine fails'],
          ['','Continue on other'],
          ['','(reduced speed)'],
        ].map(([l,v],i) => (
          <g key={i}>
            <text x={800} y={118+i*14} fontSize="8.5" fontWeight={l&&v===''?'700':'400'} fill={l?'#7a2e00':'#7a4a28'}>{l}</text>
            <text x={l&&v?848:800} y={118+i*14} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Marine twin-engine arrangements are the classic multiplex application. Each engine connects to the gearbox via a clutch, allowing either or both engines to drive the propeller shaft. Reduction gearing converts high engine speed to low propeller RPM.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Electric Motor Multiplex: Redundancy & Power Modes
// Shows N, N-1, N-2 operating states graphically
// ─────────────────────────────────────────────────────────────────────
function DiagramRedundancy() {
  const W = 960, H = 360;
  const nMotors = 4;

  // Draw a motor unit (active or failed)
  function motorUnit(cx, cy, active, idx) {
    const col = active ? '#c45a10' : '#c8b89a';
    const textCol = active ? '#fff' : '#7a4a28';
    return (
      <g>
        <circle cx={cx} cy={cy} r={28} fill={col} stroke="#7a2e00" strokeWidth={active?2.5:1.5} opacity={active?0.9:0.5}/>
        <text x={cx} y={cy-4} textAnchor="middle" fontSize="9" fontWeight="700" fill={textCol}>M{idx+1}</text>
        <text x={cx} y={cy+9} textAnchor="middle" fontSize="8" fill={textCol}>{active?'ON':'FAIL'}</text>
        {!active && (
          <g>
            <line x1={cx-16} y1={cy-16} x2={cx+16} y2={cy+16} stroke="#c0392b" strokeWidth="3"/>
            <line x1={cx+16} y1={cy-16} x2={cx-16} y2={cy+16} stroke="#c0392b" strokeWidth="3"/>
          </g>
        )}
      </g>
    );
  }

  // Three scenarios: all on, one failed, two failed
  const scenarios = [
    { title:'N = 4 (Full Power · 100%)', active:[true,true,true,true],   power:100, col:'#2a7a3b' },
    { title:'N-1 (One Failed · 75%)',    active:[true,true,true,false],  power:75,  col:'#e8b07a' },
    { title:'N-2 (Two Failed · 50%)',    active:[true,true,false,false], power:50,  col:'#c45a10' },
  ];

  const scenW = W / 3;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Redundancy States — N, N-1, N-2 Operation · Power Output vs Motor Availability</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arRD" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
          <marker id="arRDo" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#e8b07a"/>
          </marker>
          <marker id="arRDr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {scenarios.map((sc, si) => {
          const ox = si * scenW + scenW / 2;
          const motorSpacing = 58;
          const motorY = 140;

          return (
            <g key={si}>
              {/* Cell background */}
              <rect x={si*scenW+4} y={4} width={scenW-8} height={H-8}
                rx={6} fill="#fdf6ee" stroke="#e8b07a" strokeWidth="1"/>

              {/* Title */}
              <text x={ox} y={24} textAnchor="middle" fontSize="9.5" fontWeight="700" fill={sc.col}>{sc.title}</text>

              {/* Motor units */}
              {Array.from({length:nMotors}, (_,i) => {
                const mx = ox + (i - 1.5) * motorSpacing;
                return <g key={i}>{motorUnit(mx, motorY, sc.active[i], i)}</g>;
              })}

              {/* Shaft lines from active motors to combiner */}
              {Array.from({length:nMotors}, (_,i) => {
                const mx = ox + (i - 1.5) * motorSpacing;
                if (!sc.active[i]) return null;
                return (
                  <line key={i} x1={mx} y1={motorY+28} x2={ox} y2={220}
                    stroke="#2a7a3b" strokeWidth="2.5" opacity="0.7"/>
                );
              })}

              {/* Combiner */}
              <rect x={ox-42} y={220} width={84} height={28} rx={4}
                fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
              <text x={ox} y={238} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">COMBINER</text>

              {/* Output shaft */}
              <line x1={ox} y1={248} x2={ox} y2={285}
                stroke="#7a2e00" strokeWidth="4"
                markerEnd={`url(#arRD${si===0?'':si===1?'o':'r'})`}/>

              {/* Power bar */}
              <rect x={ox-50} y={292} width={100} height={16} rx={3}
                fill="#e8c89a" stroke="#e8b07a" strokeWidth="1"/>
              <rect x={ox-50} y={292} width={sc.power} height={16} rx={3}
                fill={sc.col} opacity="0.8"/>
              <text x={ox} y={304} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">{sc.power}%</text>

              {/* Power label */}
              <text x={ox} y={326} textAnchor="middle" fontSize="9" fontWeight="700" fill={sc.col}>
                {sc.power}% Power
              </text>
              <text x={ox} y={339} textAnchor="middle" fontSize="8" fill="#7a4a28">
                {sc.active.filter(Boolean).length}/{nMotors} motors active
              </text>
            </g>
          );
        })}

        {/* Dividers */}
        <line x1={scenW} y1={10} x2={scenW} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
        <line x1={scenW*2} y1={10} x2={scenW*2} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
      </svg>
      <div style={S.dCaption}>
        With N=4 motors, the system continues to operate at 75% power after one failure (N-1) and at 50% after two failures (N-2). For safety-critical systems, N-1 continuity is the minimum requirement.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Applications Gallery (wind turbine, EV, aircraft, rolling mill)
// Four real-world multiplex motor applications
// ─────────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 960, H = 420;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Real-World Multiplex Motor Applications — Wind · EV Drivetrain · Aircraft · Rolling Mill</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arAP" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <pattern id="hatchAP" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="5" stroke="#d4874a" strokeWidth="1.2" opacity="0.4"/>
          </pattern>
        </defs>

        {/* ═══ TOP-LEFT: WIND TURBINE ═══ */}
        <text x={120} y={20} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Wind Turbine</text>
        <text x={120} y={33} textAnchor="middle" fontSize="8.5" fill="#a04010">Multiple generators / pitch drives</text>

        {/* Tower */}
        <rect x={112} y={200} width={16} height={130} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>

        {/* Nacelle */}
        <rect x={85} y={170} width={70} height={35} rx={5} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={120} y={183} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#fff">Nacelle</text>
        <text x={120} y={196} textAnchor="middle" fontSize="7"   fill="#fdf0e4">GB · Gen</text>

        {/* Rotor hub */}
        <circle cx={90} cy={187} r={10} fill="#7a2e00"/>

        {/* Three blades */}
        {[270, 30, 150].map((deg, i) => {
          const a = deg * Math.PI / 180;
          const bx = 90 + 75 * Math.cos(a), by = 187 + 75 * Math.sin(a);
          return (
            <line key={i} x1={90} y1={187} x2={bx.toFixed(1)} y2={by.toFixed(1)}
              stroke="#7a2e00" strokeWidth="8" strokeLinecap="round"/>
          );
        })}

        {/* Pitch drives label */}
        <rect x={20} y={245} width={200} height={40} rx={3} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={120} y={260} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">3 pitch drive motors</text>
        <text x={120} y={273} textAnchor="middle" fontSize="8" fill="#a04010">(one per blade, independent)</text>
        <text x={120} y={285} textAnchor="middle" fontSize="7.5" fill="#7a4a28">+ yaw motors, auxiliary drives</text>

        {/* ═══ TOP-RIGHT: MULTI-MOTOR EV ═══ */}
        <text x={360} y={20} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Electric Vehicle</text>
        <text x={360} y={33} textAnchor="middle" fontSize="8.5" fill="#a04010">Dual / quad motor all-wheel drive</text>

        {/* Car body (top view) */}
        <rect x={265} y={60} width={190} height={270} rx={20} fill="url(#hatchAP)" stroke="#7a2e00" strokeWidth="2.5"/>

        {/* Four wheels */}
        {[[265,85],[440,85],[265,295],[440,295]].map(([wx,wy],i) => (
          <rect key={i} x={wx-22} y={wy-14} width={22} height={28} rx={3}
            fill="#7a2e00" stroke="#7a2e00" strokeWidth="1.5"/>
        ))}

        {/* Front motor */}
        <rect x={300} y={95} width={80} height={36} rx={4} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={340} y={110} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">Front Motor</text>
        <text x={340} y={123} textAnchor="middle" fontSize="7.5" fill="#fdf0e4">e.g. 200 kW</text>

        {/* Rear motor */}
        <rect x={300} y={275} width={80} height={36} rx={4} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={340} y={290} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">Rear Motor</text>
        <text x={340} y={303} textAnchor="middle" fontSize="7.5" fill="#fdf0e4">e.g. 300 kW</text>

        {/* Battery pack */}
        <rect x={300} y={178} width={80} height={50} rx={4} fill="#2a6496" stroke="#7a2e00" strokeWidth="2" opacity="0.8"/>
        <text x={340} y={200} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">Battery</text>
        <text x={340} y={213} textAnchor="middle" fontSize="8" fill="#fdf0e4">Pack</text>
        <text x={340} y={224} textAnchor="middle" fontSize="7.5" fill="#fdf0e4">100 kWh</text>

        {/* Shaft lines */}
        <line x1={300} y1={113} x2={265} y2={99} stroke="#7a2e00" strokeWidth="3"/>
        <line x1={380} y1={113} x2={440} y2={99} stroke="#7a2e00" strokeWidth="3"/>
        <line x1={300} y1={293} x2={265} y2={309} stroke="#7a2e00" strokeWidth="3"/>
        <line x1={380} y1={293} x2={440} y2={309} stroke="#7a2e00" strokeWidth="3"/>

        <text x={360} y={375} textAnchor="middle" fontSize="8" fill="#7a4a28">Torque vectoring: differential</text>
        <text x={360} y={387} textAnchor="middle" fontSize="7.5" fill="#a04010">allocation between motors</text>

        {/* ═══ BOTTOM-LEFT: AIRCRAFT ═══ */}
        <text x={600} y={20} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Aircraft Actuation</text>
        <text x={600} y={33} textAnchor="middle" fontSize="8.5" fill="#a04010">Control surface dual/triple motors</text>

        {/* Wing outline */}
        <path d="M 530 120 L 500 220 L 700 240 L 680 120 Z"
          fill="url(#hatchAP)" stroke="#7a2e00" strokeWidth="2"/>
        <text x={600} y={190} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00" opacity="0.6">WING</text>

        {/* Aileron */}
        <rect x={510} y={205} width={150} height={28} rx={3} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={585} y={223} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fff">AILERON</text>

        {/* Two actuators */}
        <rect x={535} y={170} width={32} height={36} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        <text x={551} y={190} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">ACT</text>
        <text x={551} y={202} textAnchor="middle" fontSize="7" fill="#7a2e00">1</text>

        <rect x={610} y={170} width={32} height={36} rx={3} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        <text x={626} y={190} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">ACT</text>
        <text x={626} y={202} textAnchor="middle" fontSize="7" fill="#7a2e00">2</text>

        {/* Actuator shafts */}
        <line x1={551} y1={206} x2={551} y2={205} stroke="#7a2e00" strokeWidth="2"/>
        <line x1={626} y1={206} x2={626} y2={205} stroke="#7a2e00" strokeWidth="2"/>

        {/* Hydraulic/electric supply lines */}
        <line x1={551} y1={170} x2={551} y2={140} stroke="#2a6496" strokeWidth="2" strokeDasharray="4,3"/>
        <line x1={626} y1={170} x2={626} y2={140} stroke="#2a6496" strokeWidth="2" strokeDasharray="4,3"/>
        <text x={575} y={135} textAnchor="middle" fontSize="7.5" fill="#2a6496">Independent supplies</text>

        <rect x={510} y={270} width={180} height={42} rx={3} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.2"/>
        <text x={600} y={286} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Dual-redundant actuators</text>
        <text x={600} y={299} textAnchor="middle" fontSize="8" fill="#a04010">Separate hydraulic circuits</text>
        <text x={600} y={311} textAnchor="middle" fontSize="7.5" fill="#7a4a28">One fails → other takes full load</text>

        {/* ═══ BOTTOM-RIGHT: ROLLING MILL ═══ */}
        <text x={840} y={20} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">Rolling Mill</text>
        <text x={840} y={33} textAnchor="middle" fontSize="8.5" fill="#a04010">Multiple drives on one roll set</text>

        {/* Top roll */}
        <ellipse cx={840} cy={160} rx={80} ry={22} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={840} y={164} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Top Roll</text>

        {/* Bottom roll */}
        <ellipse cx={840} cy={240} rx={80} ry={22} fill="#d4874a" stroke="#7a2e00" strokeWidth="2.5"/>
        <text x={840} y={244} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Bottom Roll</text>

        {/* Material being rolled */}
        <rect x={760} y={178} width={160} height={24} fill="#e8b07a" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={840} y={194} textAnchor="middle" fontSize="8" fill="#7a2e00">Strip / Slab</text>

        {/* Motors at ends of rolls */}
        <circle cx={760} cy={160} r={16} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={760} y={164} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M1</text>
        <circle cx={920} cy={160} r={16} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={920} y={164} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M2</text>
        <circle cx={760} cy={240} r={16} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={760} y={244} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M3</text>
        <circle cx={920} cy={240} r={16} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
        <text x={920} y={244} textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff">M4</text>

        <rect x={765} y={278} width={150} height={42} rx={3} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={840} y={294} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">4 motors synchronised</text>
        <text x={840} y={307} textAnchor="middle" fontSize="8" fill="#a04010">Speed matched ±0.1%</text>
        <text x={840} y={320} textAnchor="middle" fontSize="7.5" fill="#7a4a28">to prevent strip twist/tear</text>

        {/* Dividers */}
        <line x1={240} y1={10} x2={240} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
        <line x1={480} y1={10} x2={480} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
        <line x1={720} y1={10} x2={720} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>
      </svg>
      <div style={S.dCaption}>
        Multiplex motor arrangements appear wherever high power, redundancy, or precise synchronisation is required. Rolling mills synchronise multiple motors to within 0.1% to prevent strip twisting; aircraft use dual-independent actuators for flight-critical surfaces.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Performance Comparison Matrix
// Single vs multiplex across key engineering criteria
// ─────────────────────────────────────────────────────────────────────
function DiagramComparison() {
  const W = 960, H = 340;

  const configs = [
    { name:'Single Motor',        scores:[4,4,4,4,0,0,4,2,3,1], col:'#7a4a28' },
    { name:'Dual Parallel',       scores:[3,3,3,2,3,3,2,3,3,3], col:'#c45a10' },
    { name:'Triple Parallel',     scores:[2,2,2,1,4,4,1,3,2,4], col:'#d4874a' },
    { name:'N+1 Redundant',       scores:[2,2,2,1,4,4,1,4,2,4], col:'#2a6496' },
    { name:'Tandem Shaft',        scores:[3,3,3,3,3,3,3,3,3,3], col:'#2a7a3b' },
    { name:'Epicyclic Sum',       scores:[2,2,2,2,3,3,2,4,2,3], col:'#a04010' },
  ];

  const criteria = [
    'Initial cost','Physical size','Maintenance','Installation',
    'Redundancy','Scalability','Efficiency','Peak torque','Reliability','Flexibility',
  ];

  const labels = ['Poor','Fair','Good','V.Good','Excel.'];
  const colors = ['#c0392b','#d4874a','#e8b07a','#7a9e6a','#2a7a3b'];

  const lW = 130, hH = 36;
  const cellW = (W - lW) / configs.length;
  const cellH = (H - hH - 28) / criteria.length;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multiplex Configuration Comparison Matrix — Key Engineering Criteria</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Column headers */}
        {configs.map((c, j) => (
          <g key={j}>
            <rect x={lW + j*cellW + 1} y={1} width={cellW-2} height={hH-2}
              rx={3} fill={c.col} opacity="0.18"/>
            <rect x={lW + j*cellW + 1} y={1} width={cellW-2} height={hH-2}
              rx={3} fill="none" stroke={c.col} strokeWidth="1.5"/>
            <text x={lW + j*cellW + cellW/2} y={hH/2+5}
              textAnchor="middle" fontSize="8.5" fontWeight="700" fill={c.col}>{c.name}</text>
          </g>
        ))}

        {/* Rows */}
        {criteria.map((cr, i) => {
          const y = hH + i * cellH;
          return (
            <g key={i}>
              <rect x={0} y={y} width={lW-2} height={cellH-1}
                fill={i%2===0?'#fdf4ec':'#fdf6ee'} stroke="#e8c89a" strokeWidth="0.6"/>
              <text x={lW-4} y={y+cellH/2+3.5} textAnchor="end"
                fontSize="8.5" fontWeight="700" fill="#7a2e00">{cr}</text>
              {configs.map((c, j) => {
                const score = c.scores[i];
                return (
                  <g key={j}>
                    <rect x={lW + j*cellW + 1} y={y+1} width={cellW-2} height={cellH-2}
                      fill={colors[score]} rx={2} opacity="0.72"/>
                    <text x={lW + j*cellW + cellW/2} y={y+cellH/2+3.5}
                      textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#fff">{labels[score]}</text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Legend */}
        {colors.map((c, i) => (
          <g key={i}>
            <rect x={lW + i*86} y={H-22} width={12} height={12} fill={c} rx={2} opacity="0.8"/>
            <text x={lW + i*86 + 16} y={H-12} fontSize="8" fill="#7a2e00">{labels[i]}</text>
          </g>
        ))}
        <text x={lW + 5*86 + 50} y={H-12} fontSize="8" fontStyle="italic" fill="#7a4a28">← for each criterion</text>
      </svg>
      <div style={S.dCaption}>
        Single motors win on cost and simplicity. Multiplex arrangements trade these for redundancy, scalability, and flexibility. The N+1 redundant configuration offers the best reliability at moderate extra cost.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────
export default function MultiplexMotors() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Multiplex Motor Arrangement in Mechanical Engineering</h1>
      <p style={S.p}>
        A <strong>multiplex motor arrangement</strong> uses two or more motors — electric, hydraulic, or combustion — acting on a single common
        output shaft or load through a torque-combining mechanism. Rather than one large machine, multiple smaller machines share the duty,
        with their individual torques and powers summing at the output.
      </p>
      <p style={S.p}>
        This architecture is chosen for three principal reasons: to achieve output power levels beyond what a single motor of a given size can provide;
        to build in <strong>redundancy</strong> so that the system continues operating if one motor fails; and to enable <strong>load-dependent
        efficiency</strong> by switching motors in and out to maintain each running at its optimal operating point. Applications range from
        twin-engine marine vessels to multi-motor electric vehicles, aircraft actuators, and industrial rolling mills.
      </p>

      <h2 style={S.h2}>Concept — Single vs Multiplex</h2>
      <DiagramConcept />
      <Table2 rows={[
        ['Definition','A multiplex (or multi-motor) arrangement connects N motors mechanically or electrically to act on one common load\nTotal output torque: τ_total = Σ τᵢ for i=1..N\nTotal output power: P_total = Σ Pᵢ (minus combining losses η_combiner)\nEach individual motor is smaller, lighter, and often drawn from standard stock sizes'],
        ['Why not one big motor?','Very large motors: expensive, long lead time, difficult to transport and install\nNo redundancy: single point of failure halts entire system\nPoor part-load efficiency: large motor at 10% load operates far from its best efficiency point\nMultiplex: run 1 of 4 motors at full load instead of 1 motor at 25% load → far more efficient'],
        ['Power density','Multiplex allows aggregate power densities exceeding single-motor limits per installation volume\nParallel motors may be distributed spatially (e.g. one motor per axle in an EV)\nWeight distribution: multiple small units distributed vs one heavy unit centralised\nModular replacement: swap one motor without removing/replacing the entire drivetrain'],
        ['N+1 redundancy','Specify N+1 motors for critical applications: system rated for N motors, one spare always installed\nAny single motor can fail without loss of full-rated output\nAllows hot-swap maintenance: failed motor replaced while system continues operating\nCost of N+1 vs reliability improvement must be evaluated against downtime cost'],
        ['Standards & ratings','IEC 60034: rotating electrical machines — motor ratings and performance\nISO 1940: balancing grades for rotating machinery\nABS/DNV-GL: marine multiplex drive requirements (ship classification societies)\nAerospace DO-178C / ARP4754: redundancy and fault analysis requirements for aircraft systems'],
      ]}/>

      <h2 style={S.h2}>Torque-Summing Topologies</h2>
      <DiagramTorqueSumming />
      <Table2 rows={[
        ['Parallel shaft gear summing','Two or more input pinions mesh with a common output gear\nTorques add at the output gear: τ_out = (τ₁ + τ₂) × (R_out/R_in)\nSpeed constraint: all input shafts must run at same speed (rigid coupling condition)\nIf motor speeds differ slightly → circulating torque (one motor fights the other)\nSolution: speed compensation via droop control or flexible couplings between motors'],
        ['Epicyclic (planetary) summing','Sun and ring gear inputs; carrier provides combined output (or other port combinations)\nTorque relationship: τ_carrier = τ_sun × (1 + R_ring/R_sun)\nAllows differential input speeds — more flexible than parallel shaft\nUsed in: helicopter main gearboxes (multi-engine), automotive hybrid drivetrains\nNVH advantage: smooth torque transmission with high ratio achievable in compact package'],
        ['Tandem (series) shaft','Two motors mounted coaxially, second motor has hollow rotor allowing shaft to pass through\nNo combining gearbox required — second motor simply adds torque to the shared shaft\nAdvantage: axial simplicity, no gear losses in combining stage\nDisadvantage: rotor of rear motor must carry full torque of front motor through the shaft\nApplication: some submarine propulsion systems, large industrial fans'],
        ['Torque differential','Bevel gear differential: two inputs sum at a ring gear output (or vice versa)\nAllows speed inequality between inputs — useful for variable-speed multi-source drives\nUsed in: some helicopter tail rotor drives, power-split hybrid transmissions\nCan also be used to subtract torques (one input opposes the other) for speed summing'],
        ['Electric parallel (common bus)','Multiple electric motors driven from same DC/AC bus, mechanically coupled to same shaft\nTorque sharing controlled electronically: each motor inverter commanded to target torque\nNo mechanical combining gearbox — electrical bus is the combining mechanism\nUsed in: multi-motor electric vehicles, rail traction (multiple traction motors per bogie)'],
        ['Hydraulic manifold summing','Multiple hydraulic motors connected to same shaft, fed from one or more hydraulic circuits\nTorques add naturally — hydraulic motors are torque devices, not speed devices\nFlow dividers or pressure compensation ensure balanced flow to each motor\nApplication: heavy machinery (excavators, winches), marine thrusters, steering systems'],
      ]}/>

      <h2 style={S.h2}>Load Sharing &amp; Torque Balance</h2>
      <DiagramLoadSharing />
      <Table2 rows={[
        ['The load sharing problem','When two motors drive one shaft, they must operate at exactly the same speed\nIf their speed-torque characteristics differ → one motor dominates, the other is underloaded\nExtreme case: stiffer motor carries all load, compliant motor idles → defeats purpose of multiplex\nSolution: match droop characteristics, or use active electronic load-sharing control'],
        ['Speed droop','Droop = (ω_no_load − ω_full_load) / ω_no_load × 100%\nTypical electric motor droop: 2–5% (depends on motor type and controller)\nIdeal for parallel operation: all motors have identical droop → equal load sharing naturally\nDiesel engines: 3–5% droop per governor design — must be matched for parallel gensets'],
        ['Cross-current / torque compensation','Electronic control: each drive reads its own current (proportional to torque)\nCross-coupling: compares individual currents to group average, trims speed setpoint\nResult: all motors share load equally regardless of characteristic differences\nResponse time: must be faster than load change dynamics — typically 10–100 ms bandwidth'],
        ['Master-slave load sharing','One drive designated master: controls speed of the system\nAll other drives follow master: receive torque (current) reference from master\nAdvantage: simple, robust, well-understood\nDisadvantage: master drive is single point of failure for load sharing (not for operation)\nUsed in: paper machines, strip processing lines, winder/unwinder systems'],
        ['Speed control vs torque control','Speed-controlled motors: each maintains own speed → load shares by droop\nTorque-controlled motors: each receives torque reference → no speed fighting\nFor rigid coupling: torque control mode essential to prevent circulating currents\nFor flexible (belt/chain) coupling: speed control acceptable with matched droop'],
        ['Circulating currents','If two identical motors run at slightly different no-load speeds → one drives the other as generator\nCirculating current flows at zero load — causes heating without doing useful work\nMagnitude: I_circ = (ΔV) / (Z_motor1 + Z_motor2)\nPrevention: matched motors, cross-current compensation, or small mechanical compliance (flexible couplings)'],
      ]}/>

      <h2 style={S.h2}>Marine Twin-Engine Arrangement</h2>
      <DiagramMarineArrangement />
      <Table2 rows={[
        ['CODAD / CODAD configuration','CODAD: Combined Diesel And Diesel — two diesel engines on one shaft via combining gearbox\nCODAG: Combined Diesel And Gas — diesel + gas turbine sharing a shaft\nCOGAG: Combined Gas And Gas — two gas turbines (common in naval vessels for high speed)\nEach configuration optimised for different speed/efficiency operating profiles'],
        ['Combining gearbox','Accepts inputs from multiple prime movers at different speeds, outputs one shaft speed\nHelical gears: low noise, high efficiency (>98% per mesh) · used in most marine gearboxes\nSplit power path: each engine drives own gear train, both mesh a common bull gear\nClutch on each engine: allows independent engagement/disengagement'],
        ['Single-shaft vs twin-shaft','Single shaft: both engines combine to one propeller — compact but less manoeuvrability\nTwin shaft: each engine drives its own propeller — best redundancy, superior manoeuvring\nTwin-screw vessels: can stop one propeller while running other for slow harbour manoeuvring\nAzimuthing thrusters: combine multiplex engine with steerable propulsor for maximum flexibility'],
        ['Clutch types','Dry friction: simplest, cheapest — used on smaller vessels\nWet clutch (oil-immersed): higher load capacity, smoother engagement\nSynchronous (dog) clutch: positive engagement only at synchronised speed — for controllable-pitch propellers\nElastic coupling: accommodates torsional vibration and thermal expansion between engine and gearbox'],
        ['Part-load operation','Twin-engine: cruise on one engine → fuel consumption halved (approximately)\nOptimal loading: run one engine at 85–90% MCR rather than two at 50% each\nFuel saving: at 50% load, one-engine operation saves 15–25% fuel vs two engines at 50%\nMaintenance window: change oil/filters on one engine while the other maintains vessel operation'],
        ['Torsional vibration','Multi-engine arrangements create complex torsional excitation frequencies\nCombining gearbox must be designed to avoid resonances at operating speeds\nTorsional analysis: Campbell diagram — plot excitation frequencies vs rpm, identify crossings\nMitigation: tuned mass dampers, flexible couplings, varying engine firing order'],
      ]}/>

      <h2 style={S.h2}>Redundancy &amp; Fault Tolerance</h2>
      <DiagramRedundancy />
      <Table2 rows={[
        ['N, N-1, N-2 definitions','N: total number of installed motors\nN-1: one motor failed → N-1 remain → system continues at (N-1)/N × rated power\nN-2: two motors failed → system continues at (N-2)/N × rated power\nDesign requirement usually stated as minimum power level after specified number of failures'],
        ['Fault detection','Current monitoring: loss of current in one motor → controller detects and compensates\nTemperature sensors: motor overheating → pre-failure warning before breakdown\nVibration analysis: bearing/winding degradation detected early → planned maintenance\nEncoder/resolver: speed feedback loss → drive fault, switches to remaining motors'],
        ['Automatic transfer','On motor failure detection: remaining motors increase torque setpoints proportionally\nTransition time: typically <100 ms for electronic switching, <1 s for mechanical clutching\nTransient load spike: remaining motors absorb extra load → check thermal capacity for short-term overload\nDrive rating: each drive must support overload of (N/(N-1)) × rated current for at least 60 s'],
        ['Hot standby vs cold standby','Hot standby: spare motor runs at zero torque, connected and spinning — instant takeover\nCold standby: spare motor off, must be started and synchronised — slower takeover (~10–60 s)\nHot standby: higher energy cost (spinning losses) but fastest fault response\nCold standby: more economical, suitable when short power interruption is acceptable'],
        ['Common cause failures','Multiple motors can fail for the same reason: flooding, fire, power supply loss, software fault\nAvoid: single power bus for all motors — use independent supplies for each\nPhysical separation: motors in different compartments/cabinets reduces common cause vulnerability\nSoftware: validated independent control channels, not common software running all motors'],
        ['FMEA and reliability','Failure Modes and Effects Analysis: identify each motor failure mode and system effect\nReliability calculation: R_system = 1 - P(all N motors fail) for independent failures\nFor N identical motors, each with reliability R: R_k-of-N = Σ C(N,i)Rⁱ(1-R)^(N-i) for i=k..N\nExample: 4 motors each R=0.99 → 3-of-4 system reliability = 99.994%'],
      ]}/>

      <h2 style={S.h2}>Applications</h2>
      <DiagramApplications />
      <Table2 rows={[
        ['Electric vehicles (multi-motor)','Dual motor: separate front/rear axle motors — torque vectoring possible\nQuad motor: one per wheel — maximum torque vectoring, regenerative braking per wheel\nTorque vectoring: differential torque allocation between motors actively stabilises vehicle dynamics\nEfficiency: motors operate closer to peak efficiency point across the driving cycle\nRedundancy: one motor failure → continue at reduced performance, not stranded'],
        ['Wind turbines','Multiple pitch drive motors: one per blade (usually 3) — independent pitch control\nPitch motor multiplex: if one pitch motor fails, blade can be emergency-feathered by others\nYaw drives: multiple motors distributed around yaw ring to distribute yaw load\nGrid-forming inverters: multiple power converters in parallel on one turbine for redundancy\nMain generator: some designs use multiple generators on the main shaft'],
        ['Aircraft systems','Flight control actuators: dual/triple redundant actuators per control surface\nACTUATOR architectures: EHA (electrohydrostatic), EMA (electromechanical) in multiplex\nPumps: hydraulic pump multiplex — engine-driven + electrically-driven for ground/emergency\nFAA/EASA certification: must demonstrate no single failure causes loss of flight control\nPower management: multiple generators (engine + APU + RAT) ensure continuous electrical supply'],
        ['Industrial rolling mills','Tandem mill: multiple roll stands in series, each driven by synchronised motor group\nSpeed synchronisation tolerance: ±0.1% between stands — strip tears if stands desynchronise\nTwin drives per roll: one motor on each end of roll — prevents roll bending under load\nDigital speed reference: master reference signal distributed to all drive controllers simultaneously\nContinuous casting: multiplex drives maintain consistent withdrawal speed for product quality'],
        ['Pumping stations','Multiple pump-motor sets in parallel: provides staged capacity and redundancy\nVariable speed drives: each motor independently speed-controlled for energy efficiency\nN+1 duty-standby: N pumps meet demand, 1 standby auto-starts on any duty pump failure\nLoad cycling: rotate duty assignment to equalise wear and running hours across pump set\nPressure control: PID loop adjusts pump speeds to maintain constant header pressure'],
        ['Elevators and hoists','Multiple hoist motors: used on heavy-lift cranes (100+ tonnes) for balanced load distribution\nTandem drums: two motor-drum sets on one shaft for rope fleet management\nSafety brakes: independent brakes per motor — any motor failure → brake applied automatically\nSynchronisation: multi-drum hoists must maintain exact speed to prevent rope overload\nOil rig drawworks: typically 2–4 large DC or AC motors in multiplex for 2000+ HP output'],
      ]}/>

      <h2 style={S.h2}>Configuration Comparison</h2>
      <DiagramComparison />
      <Table2 rows={[
        ['Design trade-offs','Adding motors increases complexity, control system cost, and maintenance points\nBenefit grows faster than cost up to about N=3–4 in most applications\nBeyond N=4: marginal redundancy gain, control complexity dominates\nRule of thumb: use smallest N that meets redundancy and power requirements'],
        ['Mechanical combining losses','Spur/helical gear mesh: 0.5–1.5% loss per mesh\nBevel/hypoid gear: 1–3% loss per mesh\nEpicyclic: 0.5–2% depending on ratio and power split\nTotal combiner efficiency η_c typically 95–99% for well-designed systems\nLosses appear as heat — gearbox cooling must handle combiner losses at full power'],
        ['Control system architecture','Centralised: one controller commands all motor drives — simple but single point of failure\nDistributed: each drive has local intelligence, communicates via fieldbus (CANopen, EtherCAT)\nHierarchical: supervisory controller + local drive controllers — best balance\nCommunication protocols: PROFINET, EtherCAT, CANopen, Modbus TCP used in industrial multiplex\nCyber security: network-connected motor drives require security hardening — IEC 62443'],
        ['Thermal management','Each motor generates heat independently — cooling for each must be adequate\nAt N-1 operation: surviving motors run at higher load → increased temperature rise\nOverload capacity: IEC 60034-1 defines short-term overload ratings (1.5× for 2 min typical)\nThermal model in controller: monitor motor temperature, limit torque if temperature approaches limit\nCooling system: may also be multiplex — dual cooling fans, dual heat exchangers for critical applications'],
        ['Synchronisation challenges','All motors on rigid shaft: must rotate at identical speed — any difference → circulating torque\nSpeed matching tolerance: typically require <0.5% speed difference at coupling\nElastic coupling: absorbs small speed differences, reduces shock loads during engagement\nPhasing: for multi-pole motors, electrical phase alignment at coupling affects smooth engagement\nStarting sequence: motors started in sequence or simultaneously depending on inertia and power supply capacity'],
      ]}/>

      <InfoBox title="Key Formulae &amp; Relationships" items={[
        "Total output torque:   τ_total = η_combiner × Σᵢ τᵢ   (sum of all motor torques × combiner efficiency)",
        "Total output power:    P_total = η_combiner × Σᵢ Pᵢ   (sum of all motor powers)",
        "N-1 power fraction:    P_N-1 = (N-1)/N × P_rated   (one motor failed, others at rated load)",
        "Speed droop:           δ = (ω_NL - ω_FL) / ω_NL × 100%   (typical: 2–5% for electric, 3–5% diesel)",
        "Load sharing (equal):  τᵢ = τ_total / N   (requires matched droop or active sharing control)",
        "Epicyclic torque:      τ_carrier = τ_sun × (1 + Z_ring/Z_sun)   (ring gear fixed)",
        "Circulating current:   I_circ = ΔV / (Z₁ + Z₂)   (from speed mismatch between parallel motors)",
        "Reliability (N motors, k-of-N): R = Σᵢ₌ₖᴺ C(N,i) × Rⁱ × (1-R)^(N-i)",
        "Combiner gear efficiency: η_total = η₁ × η₂ × ... × ηₙ   (product of each mesh efficiency)",
        "Overload factor at N-1: OL = N/(N-1)   (e.g. 4-motor system: 4/3 = 133% of rated torque per motor)",
        "Part-load efficiency gain: BSFC improves ~15-25% running 1 engine at 85% vs 2 engines at 50%",
        "Thermal rating (overload): IEC 60034-1 allows 1.5× rated current for 2 min, 1.25× for 60 min",
      ]}/>
    </div>
  );
}